import { KSpan } from "kahuna-base-react-components"
import React, { useEffect, useRef } from "react"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import { toast } from "react-toastify"
import { getUnreadNotificationsNumber, getNotifications, markAsRead } from "../../../actions/notificationsActions"
import { NOTIFICATION_TYPES, successFilledToastOptions } from "../../../constants"
import { buttonConnectedURL, notificationContent } from "../../../utility"
import { UserNotification } from "../../../types"

const Notifications = ({
  setOpen,
  unreadNotificationsNumber,
  paginatedNotifications,
  getUnreadNotificationsNumber,
  getNotifications,
  markAsRead
}: {
  setOpen: (open: boolean) => void
  unreadNotificationsNumber: number
  paginatedNotifications: { results: UserNotification[] }
  getUnreadNotificationsNumber: () => Promise<any>
  getNotifications: (pageNumber: number) => Promise<any>
  markAsRead: (id: string) => Promise<any>
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLDivElement | null>(null)
  const seenApprovalNotificationIds = useRef<Set<string>>(new Set())
  const hasSeededApprovalNotifications = useRef(false)

  useEffect(() => {
    // Function to close the dropdown if clicked outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }

    // Add event listener for clicks
    document.addEventListener("mousedown", handleClickOutside)

    getUnreadNotificationsNumber()
    const intervalId = setInterval(() => {
      getUnreadNotificationsNumber()
    }, 1000 * 60 * 15) // 15 minutes

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    const intervalId = setInterval(() => {
      getNotifications(1)
    }, 10000)

    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    const results = paginatedNotifications?.results || []

    // Don't toast notifications that already existed before this component mounted.
    if (!hasSeededApprovalNotifications.current) {
      results.forEach((notification) => seenApprovalNotificationIds.current.add(notification.id.toString()))
      hasSeededApprovalNotifications.current = true
      return
    }

    results.forEach((notification) => {
      const id = notification.id.toString()
      if (
        notification.notificationId === NOTIFICATION_TYPES.RELEASE_EDIT_REQUEST_APPROVED &&
        !notification.read &&
        !seenApprovalNotificationIds.current.has(id)
      ) {
        seenApprovalNotificationIds.current.add(id)
        const linkUrl = buttonConnectedURL(notification)
        const markReadAndSync = () => markAsRead(id).then(() => getUnreadNotificationsNumber())

        toast.success(notificationContent(notification), {
          ...successFilledToastOptions,
          autoClose: false,
          closeButton: true,
          // window.location.href navigates immediately, which can abort the markAsRead
          // request mid-flight - wait for it to finish before leaving the page.
          onClick: linkUrl ? () => markReadAndSync().finally(() => (window.location.href = linkUrl)) : undefined,
          onClose: markReadAndSync,
          style: {
            ...successFilledToastOptions.style,
            cursor: linkUrl ? "pointer" : "default"
          }
        })
      }
    })
  }, [paginatedNotifications])

  return (
    <div className="flex items-center justify-center relative">
      <div
        ref={buttonRef}
        className="flex items-center justify-center cursor-pointer"
        style={{
          borderRadius: "10px",
          height: "36px",
          width: "36px",
          padding: "8px"
          //boxShadow: "rgba(17, 17, 17, 0.04) 0px 0px 0px 1px, rgba(17, 17, 17, 0.04) 0px 1px 1px 0px"
        }}
        onClick={() => {
          setOpen(true)
        }}
      >
        <div className="relative">
          <img src="/notification.svg" alt="notification-icon" />
          {unreadNotificationsNumber > 0 && (
            <div className="absolute w-4 h-4 flex items-center justify-center top-[-4px] right-[-4px] bg-[#FF5865] rounded-full">
              <KSpan
                text={unreadNotificationsNumber.toString()}
                color="#FFF"
                fontSize={8}
                lineHeight="12px"
                fontWeight={500}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    unreadNotificationsNumber: state.notifications.unreadNotificationsNumber,
    paginatedNotifications: state.notifications.paginatedNotifications
  }
}

export default connect(mapStateToProps, { getUnreadNotificationsNumber, getNotifications, markAsRead })(
  Notifications
)
